import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { useMangoApiClient } from '~/atoms/MangoApi'

import {
  ApiError,
  MessageSnippet,
  MessageSnippetPostResponse,
  MessageSnippetRequest,
  PaginatedApiResult,
} from '@mangovoice/types'

const SnippetsQueryKeysEnum = {
  SNIPPETS: 'snippets',
}

export const useGetSnippets = (
  pbxId: number | undefined,
  offset: string,
  personal: boolean,
  onQuerySuccess:
    | ((data: PaginatedApiResult<MessageSnippet>) => void)
    | undefined = undefined,
  options: Record<string, unknown> = {},
) => {
  const api = useMangoApiClient()
  return useQuery({
    queryKey: [SnippetsQueryKeysEnum.SNIPPETS, { pbxId, offset, personal }],
    queryFn: async () => {
      const { data, ok } = await api.messaging.getSnippets({
        offset: offset ? Number(offset) : undefined,
        personal,
        pbxId,
      })
      if (ok) {
        onQuerySuccess?.(data as PaginatedApiResult<MessageSnippet>)
      }
      return data as PaginatedApiResult<MessageSnippet>
    },
    ...options,
    retry: (options.retry as number | boolean | undefined) ?? false,
  })
}

export const useSearchSnippets = (
  search: string,
  onQuerySuccess:
    | ((data: PaginatedApiResult<MessageSnippet>) => void)
    | undefined = undefined,
  options: Omit<
    UseQueryOptions<PaginatedApiResult<MessageSnippet>, ApiError>,
    'queryKey'
  >,
) => {
  const api = useMangoApiClient()
  return useQuery<PaginatedApiResult<MessageSnippet>, ApiError>({
    queryKey: [SnippetsQueryKeysEnum.SNIPPETS, { search }],
    queryFn: async () => {
      const { ok, data } = await api.messaging.searchSnippets(search)
      if (!ok) {
        return Promise.reject(data)
      }
      onQuerySuccess?.(data as PaginatedApiResult<MessageSnippet>)
      return data!
    },
    ...options,
    retry: (options.retry as number | boolean | undefined) ?? false,
  })
}

export const useDeleteSnippet = (
  onSuccess: (data: undefined, snippetId: number) => void,
) => {
  const api = useMangoApiClient()
  return useMutation({
    mutationFn: async (snippetId: number) => {
      const { ok, data } = await api.messaging.deleteSnippet(snippetId)
      if (!ok) {
        return Promise.reject(data!)
      }
      return undefined
    },
    onSuccess: onSuccess,
  })
}

export type MessageSnippetUpsertRequest = MessageSnippetRequest & {
  id: number | undefined
}
export const useSaveSnippet = (
  onSuccess: (
    data: MessageSnippetPostResponse,
    snippet: MessageSnippetUpsertRequest,
  ) => void,
  onError: (error: ApiError, snippet: MessageSnippetUpsertRequest) => void,
) => {
  const api = useMangoApiClient()
  return useMutation({
    mutationFn: async (snippet: MessageSnippetUpsertRequest) => {
      const { id, ...payload } = snippet
      const { ok, data } = id
        ? await api.messaging.updateSnippet(id, payload)
        : await api.messaging.postSnippet(payload)
      if (!ok || !data) {
        return Promise.reject(data)
      }
      return data
    },
    onSuccess: onSuccess,
    onError: onError,
  })
}
